import { useCallback, useEffect, useRef, useState } from 'react'
import { useStudioStore } from '../../store/studio-store'
import { useUIBreakpoint } from '@/hooks/use-ui-breakpoint'
import { useDispatchValidationBridge } from '@/features/validation-bridge'
import { ChatDrawerSlot } from '../chat-drawer-slot'
import { ChatNoticeSection, ChatWorkChoiceSection } from '../chat-panel'
import { ResponsivePanel } from '../responsive-panel'
import { ChatHeader } from './components/chat-header'
import { ChatMessages } from './components/chat-messages'
import { ChatRailInputBar } from './components/chat-rail-input-bar'
import { AgentCollaborationSummary } from './components/agent-collaboration-summary'
import { IntentPreviewHost } from '@/features/agent/components/intent-preview-host'
import { useIntentPreview } from '@/features/agent/hooks/use-intent-preview'
import { clampChatPanelWidth } from '@/store/ui-slice'
import { SelectionContextPanel } from './selection-context-panel'
import { StudioTaskCenter } from './components/studio-task-center'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from './coogo-ide-chrome-copy'
import { getChatMessagesCopy } from './components/chat-messages-copy'
import { humanizeFailureReason } from './components/miss-reason-copy'
import { TemplateContentPanel } from '@/features/templates/content-form/TemplateContentPanel'
import { deriveStudioInteractionCapabilities } from './studio-interaction-capabilities'
import { StudioInteractionTabs } from './components/studio-interaction-tabs'
import { useContextualChatDisclosure } from '../use-contextual-chat-disclosure'
import { ProjectLoadErrorChatPanel } from './components/project-load-error-chat-panel'
import {
  PROJECT_CHAT_OWNED_MESSAGE_IDS,
  useProjectChatThreadHydration,
} from '@/features/chat/components/use-project-chat-thread-hydration'
import {
  useProjectChatThreadRetryHandler,
  type ProjectChatThreadRetryExecution,
} from '@/features/chat/components/use-project-chat-thread-retry-handler'
import {
  appendProjectChatThreadTurn,
  projectChatOutcomeIdForSourceMessage,
  settleProjectChatThreadOutcome,
  type ProjectChatPrimaryRoute,
  type ProjectChatThreadScope,
} from '@/features/chat/project-chat-thread'
import {
  useChannelDispatch,
  type ChannelDispatchResult,
  type ChatMessage,
} from '@/features/channel-router'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'

const BUILD_RETRY_ROUTE_LABEL: Record<
  LocaleCode,
  Record<ProjectChatPrimaryRoute, string>
> = {
  en: {
    'read-only': 'project question',
    'grounded-edit': 'project edit',
    generation: 'generation',
    'agent-runtime': 'agent task',
    'studio-operation': 'Studio operation',
  },
  ko: {
    'read-only': '프로젝트 질문',
    'grounded-edit': '프로젝트 편집',
    generation: '생성',
    'agent-runtime': '에이전트 작업',
    'studio-operation': 'Studio 작업',
  },
  ja: {
    'read-only': 'プロジェクトの質問',
    'grounded-edit': 'プロジェクト編集',
    generation: '生成',
    'agent-runtime': 'エージェントタスク',
    'studio-operation': 'Studio 操作',
  },
  zh: {
    'read-only': '项目问题',
    'grounded-edit': '项目编辑',
    generation: '生成',
    'agent-runtime': '代理任务',
    'studio-operation': 'Studio 操作',
  },
  'zh-TW': {
    'read-only': '專案問題',
    'grounded-edit': '專案編輯',
    generation: '生成',
    'agent-runtime': '代理任務',
    'studio-operation': 'Studio 操作',
  },
}

const BUILD_RETRY_COPY: Record<
  LocaleCode,
  {
    pending: string
    interrupted: string
    unavailable: (routeLabel: string) => string
  }
> = {
  en: {
    pending: 'Retrying the saved generation request.',
    interrupted: 'This retry stopped because the active project changed.',
    unavailable: (routeLabel) =>
      `This saved ${routeLabel} needs the full Chat editor. Switch to Edit mode and try again.`,
  },
  ko: {
    pending: '저장된 생성 요청을 다시 시도하고 있습니다.',
    interrupted: '활성 프로젝트가 변경되어 재시도가 중단되었습니다.',
    unavailable: (routeLabel) =>
      `저장된 ${routeLabel}은(는) 전체 채팅 편집기가 필요합니다. 편집 모드로 전환한 후 다시 시도하세요.`,
  },
  ja: {
    pending: '保存された生成リクエストを再試行しています。',
    interrupted: 'アクティブなプロジェクトが変更されたため、再試行を停止しました。',
    unavailable: (routeLabel) =>
      `保存された${routeLabel}には完全なチャットエディターが必要です。編集モードに切り替えて、もう一度お試しください。`,
  },
  zh: {
    pending: '正在重试已保存的生成请求。',
    interrupted: '由于活动项目已更改，此重试已停止。',
    unavailable: (routeLabel) =>
      `已保存的${routeLabel}需要完整的聊天编辑器。请切换到编辑模式后重试。`,
  },
  'zh-TW': {
    pending: '正在重試已儲存的生成請求。',
    interrupted: '由於使用中的專案已變更，此重試已停止。',
    unavailable: (routeLabel) =>
      `已儲存的${routeLabel}需要完整的聊天編輯器。請切換到編輯模式後重試。`,
  },
}

function currentProjectChatScope(): ProjectChatThreadScope | null {
  const studio = useStudioStore.getState()
  const userId = studio.session?.user?.id ?? null
  const projectId = studio.currentProjectId ?? null
  return userId && projectId ? { userId, projectId } : null
}

function sameProjectChatScope(
  left: ProjectChatThreadScope | null,
  right: ProjectChatThreadScope | null,
): boolean {
  return Boolean(
    left &&
      right &&
      left.userId === right.userId &&
      left.projectId === right.projectId,
  )
}

/**
 * Intent Loop right rail — persistent user/agent collaboration surface.
 *
 * Phase A A3.3: Build mode = ChatMessages (router-driven).
 * Edit mode = legacy ChatDrawerSlot.
 *
 * Phase D: BuildModeChat mounts useDispatchValidationBridge to emit
 * validation-summary + validation-issue messages after each dispatch.
 *
 * Level 1 intent UX: the rail is not preview-only. It follows the user across
 * Pages, Logic, Data, Proof, Report, and Release surfaces so the active request,
 * proposed patch, validation, and approval loop stay attached to the task.
 */
export function ChatRail() {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const studioMode = useStudioStore((s) => s.studioMode)
  const dispatchedOutput = useStudioStore((s) => s.dispatchedOutput)
  const currentProject = useStudioStore((s) => s.currentProject)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const projectLoadError = useStudioStore((s) => s.projectLoadError)
  const chatSidebarOpen = useStudioStore((s) => s.chatSidebarOpen)
  const intentPreview = useIntentPreview()
  const handleChatOpenChange = useContextualChatDisclosure(
    Boolean(intentPreview && !intentPreview.resolved),
  )
  const chatPanelWidth = useStudioStore((s) => clampChatPanelWidth(s.chatPanelWidth))
  const setChatPanelWidth = useStudioStore((s) => s.setChatPanelWidth)
  // D1 (operator-reported overlap, design-review 2026-07-27) — mirror the
  // ChatDrawerSlot contract: inline (space-reserving) from lg so the
  // 1024–1279px band never renders the Sheet overlay on top of the canvas;
  // cap the inline width at 360px on lg so the canvas keeps usable width.
  const { bp } = useUIBreakpoint()
  const inlineChatPanelWidth =
    bp === 'lg' ? Math.min(chatPanelWidth, 360) : chatPanelWidth
  // Keep the persisted width in lockstep with the rendered (lg-capped) width —
  // see ChatDrawerSlot for the rubber-band rationale.
  const handleChatPanelResize = (width: number) =>
    setChatPanelWidth(bp === 'lg' ? Math.min(width, 360) : width)
  const setChatSidebarOpen = useStudioStore((s) => s.setChatSidebarOpen)
  const capabilities = deriveStudioInteractionCapabilities({
    currentProjectId,
    currentProject,
    projectMetadata: null,
  })
  const hasProjectConversation =
    dispatchedOutput !== null || capabilities.hasProjectConversation
  const {
    dispatch: dispatchBuildRetry,
    abort: abortBuildRetry,
    isRunning: buildRetryRunning,
  } = useChannelDispatch()
  const buildRetrySubmissionInFlightRef = useRef(false)
  const submitBuildRetry = useCallback(
    async ({ scope, route, intent }: ProjectChatThreadRetryExecution) => {
      const copy = BUILD_RETRY_COPY[locale]
      const channelCopy = getChatMessagesCopy(locale)
      const appendVisibleUser = (message?: Extract<ChatMessage, { kind: 'user' }>) => {
        const turn = {
          kind: 'user' as const,
          id: message?.id ?? `chat-user-${crypto.randomUUID()}`,
          timestamp: message?.timestamp ?? Date.now(),
          route,
          text: message?.text ?? intent,
        }
        const visible = Boolean(message) || sameProjectChatScope(scope, currentProjectChatScope())
        if (!message && visible) {
          useStudioStore.getState().appendChatMessage({
            kind: 'user',
            id: turn.id,
            timestamp: turn.timestamp,
            text: turn.text,
          })
        }
        appendProjectChatThreadTurn(scope, turn)
        if (visible) PROJECT_CHAT_OWNED_MESSAGE_IDS.add(turn.id)
        return turn
      }

      const appendPendingOutcome = (userTurnId: string) => {
        const id = projectChatOutcomeIdForSourceMessage(userTurnId)
        appendProjectChatThreadTurn(scope, {
          kind: 'outcome',
          id,
          timestamp: Date.now(),
          route,
          status: 'pending',
          text: copy.pending,
          retry: { retryable: false, route, intent },
        })
        return id
      }

      if (route !== 'generation') {
        const userTurn = appendVisibleUser()
        const outcomeId = appendPendingOutcome(userTurn.id)
        const text = copy.unavailable(BUILD_RETRY_ROUTE_LABEL[locale][route])
        settleProjectChatThreadOutcome(scope, outcomeId, {
          status: 'failed',
          text,
          retryable: true,
          timestamp: Date.now(),
        })
        const visibleOutcome: ChatMessage = {
          kind: 'project-chat-outcome',
          id: outcomeId,
          timestamp: Date.now(),
          route,
          status: 'failed',
          text,
          retry: { retryable: true, route, intent },
        }
        if (sameProjectChatScope(scope, currentProjectChatScope())) {
          useStudioStore.getState().appendChatMessage(visibleOutcome)
          PROJECT_CHAT_OWNED_MESSAGE_IDS.add(outcomeId)
        }
        return
      }

      let userTurnId: string | null = null
      let outcomeId: string | null = null
      let scopeInterrupted = false
      const observeMessage = (message: ChatMessage) => {
        if (!sameProjectChatScope(scope, currentProjectChatScope())) {
          // Dispatch emits before it observes. If scope changed in between,
          // remove the captured-scope emission immediately instead of letting
          // it appear in the newly active project.
          useStudioStore.setState({
            chatMessages: useStudioStore
              .getState()
              .chatMessages.filter((candidate) => candidate.id !== message.id),
          })
          return
        }
        if (message.kind === 'ai-result-failure') {
          // The durable outcome owns Retry authority in build mode. Suppress
          // the channel bubble synchronously so its direct Retry cannot bypass
          // route, scope, ownership, settlement, or reload persistence.
          useStudioStore.setState({
            chatMessages: useStudioStore
              .getState()
              .chatMessages.filter((candidate) => candidate.id !== message.id),
          })
          return
        }
        PROJECT_CHAT_OWNED_MESSAGE_IDS.add(message.id)
        if (message.kind === 'user' && !userTurnId) {
          const userTurn = appendVisibleUser(message)
          userTurnId = userTurn.id
          outcomeId = appendPendingOutcome(userTurn.id)
        }
      }
      const unsubscribe = useStudioStore.subscribe(() => {
        if (
          !scopeInterrupted &&
          !sameProjectChatScope(scope, currentProjectChatScope())
        ) {
          scopeInterrupted = true
          if (outcomeId) {
            settleProjectChatThreadOutcome(scope, outcomeId, {
              status: 'failed',
              text: copy.interrupted,
              retryable: true,
              timestamp: Date.now(),
            })
          }
          abortBuildRetry()
        }
      })
      let result: ChannelDispatchResult
      try {
        result = await new Promise<ChannelDispatchResult>((resolve) => {
          dispatchBuildRetry(intent, {
            onMessage: observeMessage,
            onSettled: resolve,
          })
        })
      } catch (error) {
        result = {
          status: scopeInterrupted ? 'interrupted' : 'failed',
          text:
            error instanceof Error && error.message.trim()
              ? error.message.trim()
              : scopeInterrupted
                ? copy.interrupted
                : 'Generation failed.',
          messageIds: [],
          terminalMessageId: null,
        }
      } finally {
        unsubscribe()
      }

      if (!userTurnId) {
        const userTurn = appendVisibleUser()
        userTurnId = userTurn.id
        outcomeId = appendPendingOutcome(userTurn.id)
      }
      const interrupted = scopeInterrupted || result.status === 'interrupted'
      const succeeded = !interrupted && result.status === 'succeeded'
      const reason = result.text.trim()
      const humanizedFailure = humanizeFailureReason(reason || 'bind-throw', locale)
      const retryable = interrupted || (!succeeded && humanizedFailure.retryable)
      const settlementText = interrupted
        ? copy.interrupted
        : succeeded
          ? channelCopy.ai.applied
          : humanizedFailure.title
      settleProjectChatThreadOutcome(scope, outcomeId!, {
        status: succeeded ? 'succeeded' : 'failed',
        text: settlementText,
        retryable,
        timestamp: Date.now(),
      })

      if (
        (!succeeded || result.terminalMessageId === null) &&
        sameProjectChatScope(scope, currentProjectChatScope())
      ) {
        const visibleOutcome: ChatMessage = {
          kind: 'project-chat-outcome',
          id: outcomeId!,
          timestamp: Date.now(),
          route,
          status: succeeded ? 'succeeded' : 'failed',
          text: settlementText,
          retry: { retryable, route, intent },
        }
        useStudioStore.getState().appendChatMessage(visibleOutcome)
        PROJECT_CHAT_OWNED_MESSAGE_IDS.add(visibleOutcome.id)
      }
    },
    [abortBuildRetry, dispatchBuildRetry, locale],
  )
  useProjectChatThreadHydration(
    null,
    undefined,
    studioMode === 'build' && !projectLoadError && hasProjectConversation,
  )
  useProjectChatThreadRetryHandler({
    enabled:
      studioMode === 'build' && !projectLoadError && hasProjectConversation,
    // Let unavailable executors settle visibly and durably instead of turning
    // Retry into a silent no-op. The channel dispatch itself reports cold-KG
    // unavailability through onSettled; only an active run is truly busy.
    busy: buildRetryRunning,
    submissionInFlightRef: buildRetrySubmissionInFlightRef,
    submit: submitBuildRetry,
  })

  // A failed open must reveal the dock for the same reason a successful one
  // does. `openServerProject` sets `chatSidebarOpen = true` on the success
  // path; its catch sets none of that, so a creator arriving from the
  // dashboard — where the drawer default is CLOSED (shell-chat-defaults.ts),
  // and the flag is persisted across the page load — reached the failure
  // screen with `chatSidebarOpen: false` and `ResponsivePanel` rendering null
  // (measured). Keyed on the error itself, so closing the panel afterwards is
  // still the creator's decision and is not undone.
  useEffect(() => {
    if (projectLoadError) setChatSidebarOpen(true)
  }, [projectLoadError, setChatSidebarOpen])

  // A project that did not open owns the dock before any mode does (F13,
  // 2026-08-13). Both branches below would otherwise be wrong for it: the
  // build branch removes the dock entirely (`hasProjectConversation` is false
  // — there is no project id and no project), and the edit branch would mount
  // a full composer for a project that cannot be changed. This branch is FIRST
  // so the failure state is decided by the failure, not by whichever mode the
  // shell happened to be left in.
  if (projectLoadError) {
    return (
      <ResponsivePanel
        id="chat"
        side="right"
        inlineFrom="lg"
        modalFrom="md"
        open={chatSidebarOpen}
        onOpenChange={handleChatOpenChange}
        size={inlineChatPanelWidth}
        onResize={handleChatPanelResize}
        a11yTitle={copy.chat}
        a11yDescription={copy.conversation(copy.chat)}
        closeLabel={copy.close}
        sheetClassName="!w-[calc(100vw-3.5rem)] !max-w-[calc(100vw-3.5rem)] !p-0 sm:!w-[28rem] sm:!max-w-[28rem]"
      >
        <ProjectLoadErrorChatPanel />
      </ResponsivePanel>
    );
  }

  // Edit mode: delegate to the shared ChatDrawerSlot, which OWNS its own
  // <ResponsivePanel id="chat">. Do NOT add an outer ResponsivePanel here — that
  // would nest two [data-responsive-panel="chat"] nodes (Blocker-3, Phase 531).
  if (studioMode !== "build") return <ChatDrawerSlot />;

  // Keep the idle creation page clean, but never remove Chat from an existing
  // project. A persisted project has no dispatchedOutput after reload, and the
  // previous generation-only gate left the top-bar Chat toggle with no panel.
  if (!hasProjectConversation) return null;

  // Match the IDE contract at common desktop widths: chat is a stable sibling
  // panel from lg upward, while narrower workspaces retain the modal sheet.
  return (
    <ResponsivePanel
      id="chat"
      side="right"
      inlineFrom="lg"
      modalFrom="md"
      open={chatSidebarOpen}
      onOpenChange={handleChatOpenChange}
      size={inlineChatPanelWidth}
      onResize={handleChatPanelResize}
      a11yTitle={copy.chat}
      a11yDescription={copy.conversation(copy.chat)}
      closeLabel={copy.close}
      sheetClassName="!w-[calc(100vw-3.5rem)] !max-w-[calc(100vw-3.5rem)] !p-0 sm:!w-[28rem] sm:!max-w-[28rem]"
    >
      <div
        data-testid="chat-rail"
        className="h-full min-h-0 bg-card border-l border-border flex flex-col overflow-hidden animate-in slide-in-from-right duration-300"
      >
        {/* Build mode only — the edit-mode branch above returns ChatDrawerSlot,
            whose ChatPanel already renders this, so there is no double render.
            Placed on the rail rather than inside BuildModeChat because that
            component returns from three different tab branches; the rail is the
            one place every branch passes through. */}
        <ChatNoticeSection />
        <ChatWorkChoiceSection />
        <BuildModeChat />
      </div>
    </ResponsivePanel>
  );
}

function BuildModeChat() {
  useDispatchValidationBridge()
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const intentPreview = useIntentPreview()
  const inspectorPanelOpen = useStudioStore((s) => s.inspectorPanelOpen)
  const setInspectorPanelOpen = useStudioStore((s) => s.setInspectorPanelOpen)
  const studioPersona = useStudioStore((s) => s.studioPersona)
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const currentProject = useStudioStore((s) => s.currentProject)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const showCollaborationSummary = studioPersona === 'developer' && (!intentPreview || intentPreview.resolved)

  const capabilities = deriveStudioInteractionCapabilities({
    currentProjectId,
    currentProject,
    projectMetadata:
      (projectMeta?.metadata as Record<string, unknown> | undefined) ?? null,
  })
  const [contentTabOpen, setContentTabOpen] = useState(false)
  const activeTab =
    contentTabOpen && capabilities.hasTemplateContent
      ? 'content'
      : inspectorPanelOpen
        ? 'context'
        : 'chat'
  const tabStrip = (
    <StudioInteractionTabs
      tabs={capabilities.tabs}
      activeTab={activeTab}
      labels={{
        chat: copy.chat,
        context: copy.details,
        content: copy.content,
      }}
      onSelect={(tab) => {
        setContentTabOpen(tab === 'content')
        setInspectorPanelOpen(tab === 'context')
      }}
    />
  )

  if (activeTab === 'content') {
    return (
      <>
        {tabStrip}
        <TemplateContentPanel />
      </>
    )
  }

  if (activeTab === 'context') {
    return (
      <>
        {tabStrip}
        <SelectionContextPanel
          onShowChat={() => {
            setContentTabOpen(false)
            setInspectorPanelOpen(false)
          }}
        />
      </>
    )
  }

  return (
    <>
      {tabStrip}
      <ChatHeader />
      <StudioTaskCenter />
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
        <IntentPreviewHost />
        {showCollaborationSummary && <AgentCollaborationSummary />}
        <ChatMessages layout="story" hideWhenEmpty progressOnly />
      </div>
      <ChatRailInputBar />
    </>
  );
}
